import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from "react";
import {
  trackFloatingToolbarMetrics,
  type FloatingToolbarContextSnapshot,
  type FloatingToolbarMetricsSnapshot,
} from "../services/floatingToolbarTelemetry";
import { useFloatingToolbarPopout } from "./useFloatingToolbarPopout";
import type {
  AnnotationCategoryFilter,
  FloatingToolbarPosition,
} from "../components/eegViewerChromeShared";
import type { AnnotationTypeCatalogEntry } from "../components/eegViewerDisplayHelpers";
import type { AnnotationListItem } from "../components/AnnotationListView";
import type {
  QuickAction,
  QuickActionListTypeOption,
  QuickActionRecentAnnotation,
} from "../components/QuickActionToolbar";
import type { EventCatalogItem, EventSet } from "@features/EEGConfig/types/eegConfig.types";
import {
  formatAnnotationListLabel,
  getAnnotationTypeDisplayName,
} from "../utils/annotationListLabel";
import logger from "@shared/utils/logger";

const QuickActionToolbar = lazy(() => import("../components/QuickActionToolbar"));

/**
 * Floating quick-action annotation toolbar extracted from EEGViewer to keep
 * the main component focused on rendering. Owns toolbar preferences/metrics
 * persistence + hydration, popout wiring (via useFloatingToolbarPopout),
 * the quick-add action catalog builders, recent-annotation list, telemetry
 * flushing, and the render function used for both the pinned and detached
 * layouts.
 */

interface FloatingQuickToolbarPreferences {
  enabled: boolean;
  editMode: boolean;
  position: FloatingToolbarPosition;
  offsetX: number;
  offsetY: number;
  panelWidthPx: number;
  actionOrder: string[];
  hiddenActionIds: string[];
  eventSetId: string;
  categoryMode: AnnotationCategoryFilter;
}

interface FloatingQuickToolbarMetrics {
  openCount: number;
  actionCount: number;
  errorClicks: number;
  firstActionSamples: number;
  totalFirstActionMs: number;
  actionUsage: Record<string, number>;
}

interface FloatingToolbarTelemetryPayload {
  metrics: FloatingToolbarMetricsSnapshot;
  context: FloatingToolbarContextSnapshot;
}

const DEFAULT_FLOATING_TOOLBAR_PREFERENCES: FloatingQuickToolbarPreferences = {
  enabled: true,
  editMode: false,
  position: "top-right",
  offsetX: 0,
  offsetY: 0,
  panelWidthPx: 148,
  actionOrder: [],
  hiddenActionIds: [],
  eventSetId: "",
  categoryMode: "all",
};

const DEFAULT_FLOATING_TOOLBAR_METRICS: FloatingQuickToolbarMetrics = {
  openCount: 0,
  actionCount: 0,
  errorClicks: 0,
  firstActionSamples: 0,
  totalFirstActionMs: 0,
  actionUsage: {},
};

const DEFAULT_ANNOTATION_CATEGORY_FILTER: AnnotationCategoryFilter = "all";

const QUICK_ACTION_BUTTON_EMOJI: Record<string, string> = {
  seizure_onset: "🔴",
  seizure_offset: "🟢",
  artifact: "⚠️",
  medication_administered: "💉",
};

// These trivial, dependency-free helpers duplicate one-line utilities that
// also live in EEGViewer.tsx (used well beyond the floating toolbar there).
// Duplicating them here avoids threading half a dozen extra callback props
// through this hook's options for essentially no risk.
function isAnnotationCategoryFilter(
  value: unknown,
): value is AnnotationCategoryFilter {
  return (
    value === "all" || value === "clinical" || value === "technical_imported"
  );
}

function normalizeAnnotationTypeCode(value?: string | null): string {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function normalizeAnnotationCatalogLink(value?: string | null): string {
  return normalizeAnnotationTypeCode(value)
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function isTechnicalImportedAnnotationTypeCode(value?: string | null): boolean {
  const normalized = normalizeAnnotationTypeCode(value);
  return (
    normalized === "edf_import" ||
    normalized.startsWith("edf_") ||
    normalized.startsWith("technical_") ||
    normalized.includes("import")
  );
}

function formatViewerClockTime(totalSeconds: number): string {
  const clamped = Math.max(0, Number.isFinite(totalSeconds) ? totalSeconds : 0);
  const hours = Math.floor(clamped / 3600);
  const minutes = Math.floor((clamped % 3600) / 60);
  const seconds = Math.floor(clamped % 60);
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(
    seconds,
  ).padStart(2, "0")}`;
}

function formatCompactAnnotationDuration(
  startSeconds: number,
  endSeconds: number,
): string {
  const duration = Math.max(0, endSeconds - startSeconds);
  if (!Number.isFinite(duration) || duration <= 0) {
    return "0s";
  }
  if (duration < 1) {
    return `${Math.round(duration * 1000)}ms`;
  }
  if (duration < 60) {
    return `${duration.toFixed(duration < 10 ? 1 : 0)}s`;
  }
  const minutes = Math.floor(duration / 60);
  const seconds = Math.round(duration % 60);
  return `${minutes}m ${seconds}s`;
}

function catalogEntrySupportsDuration(
  entry: Pick<
    AnnotationTypeCatalogEntry,
    | "supports_duration"
    | "default_duration_seconds"
    | "min_duration_seconds"
    | "max_duration_seconds"
  >,
): boolean {
  if (entry.supports_duration) {
    return true;
  }

  if (
    typeof entry.default_duration_seconds === "number" &&
    Number.isFinite(entry.default_duration_seconds) &&
    entry.default_duration_seconds > 0
  ) {
    return true;
  }

  if (
    (typeof entry.min_duration_seconds === "number" &&
      Number.isFinite(entry.min_duration_seconds) &&
      entry.min_duration_seconds > 0) ||
    (typeof entry.max_duration_seconds === "number" &&
      Number.isFinite(entry.max_duration_seconds) &&
      entry.max_duration_seconds > 0)
  ) {
    return true;
  }

  return false;
}

function readJsonFromStorage<T>(storageKey: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as T;
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

function readFloatingToolbarPreferences(
  storageKey: string,
): FloatingQuickToolbarPreferences {
  const stored = readJsonFromStorage<Partial<FloatingQuickToolbarPreferences> | null>(
    storageKey,
    null,
  );
  if (!stored) {
    return DEFAULT_FLOATING_TOOLBAR_PREFERENCES;
  }
  return {
    ...DEFAULT_FLOATING_TOOLBAR_PREFERENCES,
    ...stored,
    enabled: Boolean(stored.enabled),
    categoryMode: isAnnotationCategoryFilter(stored.categoryMode)
      ? stored.categoryMode
      : DEFAULT_FLOATING_TOOLBAR_PREFERENCES.categoryMode,
    panelWidthPx: clampAnnotationSetWidth(stored.panelWidthPx),
  };
}

function clampAnnotationSetWidth(value: unknown): number {
  const width = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(width)) {
    return DEFAULT_FLOATING_TOOLBAR_PREFERENCES.panelWidthPx;
  }
  return Math.max(108, Math.min(280, Math.round(width)));
}

type AnnotationLike = { annotation_type: string; color?: string };

export interface UseEEGFloatingToolbarOptions {
  toolbarUserId: string;
  recordingId?: string | null;
  studyId?: string | null;
  activeEventSetId?: string | null;
  viewerEventSets: EventSet[];
  viewerEventCatalog: EventCatalogItem[];
  activeViewerEventItems: EventCatalogItem[];
  annotationListItems: AnnotationListItem[];
  annotationTypeByCode: Map<string, AnnotationTypeCatalogEntry>;
  annotationTypeCatalog: AnnotationTypeCatalogEntry[];
  requestedCurrentTime: number;
  resolveAnnotationDisplayColor: (
    annotation: AnnotationLike,
    fallbackColor?: string | null,
  ) => string;
  resolveAnnotationTypeCategory: (
    type: Pick<AnnotationTypeCatalogEntry, "type_code" | "category">,
  ) => AnnotationCategoryFilter;
  jumpToViewerTime: (
    targetTime: number,
    reason?: {
      source?: string;
      direction?: string;
      pageCount?: number;
      stepSeconds?: number;
    },
  ) => void;
  addQuickActionAnnotation: (type: AnnotationTypeCatalogEntry) => void;
  setShowAnnotationQuickOpen: (value: boolean) => void;
  setTrendPreviewMessage: (value: string | null) => void;
  setTrendPreviewError: (value: string | null) => void;
  popupBlockedTimerRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;
  setPopupBlockedMessage: (value: string | null) => void;
}

export function useEEGFloatingToolbar(options: UseEEGFloatingToolbarOptions) {
  const {
    toolbarUserId,
    recordingId,
    studyId,
    activeEventSetId,
    viewerEventSets,
    viewerEventCatalog,
    activeViewerEventItems,
    annotationListItems,
    annotationTypeByCode,
    annotationTypeCatalog,
    requestedCurrentTime,
    resolveAnnotationDisplayColor,
    resolveAnnotationTypeCategory,
    jumpToViewerTime,
    addQuickActionAnnotation,
    setShowAnnotationQuickOpen,
    setTrendPreviewMessage,
    setTrendPreviewError,
    popupBlockedTimerRef,
    setPopupBlockedMessage,
  } = options;

  const floatingToolbarPrefsStorageKey = `eeg-floating-toolbar-prefs:${toolbarUserId}`;
  const floatingToolbarMetricsStorageKey = `eeg-floating-toolbar-metrics:${toolbarUserId}`;

  const toolbarEnabledAtRef = useRef<number | null>(null);
  const [isMobileQuickToolbarViewport, setIsMobileQuickToolbarViewport] =
    useState(false);
  const [floatingToolbarPreferences, setFloatingToolbarPreferences] =
    useState<FloatingQuickToolbarPreferences>(() =>
      readFloatingToolbarPreferences(floatingToolbarPrefsStorageKey),
    );
  const [floatingToolbarMetrics, setFloatingToolbarMetrics] =
    useState<FloatingQuickToolbarMetrics>(DEFAULT_FLOATING_TOOLBAR_METRICS);
  const floatingToolbarPopout = useFloatingToolbarPopout();
  const floatingToolbarPrefsHydratedRef = useRef(false);
  const floatingToolbarTelemetryFlushTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const floatingToolbarLastTelemetryHashRef = useRef<string>("");
  const floatingToolbarPendingTelemetryRef = useRef<{
    hash: string;
    payload: FloatingToolbarTelemetryPayload;
  } | null>(null);

  useEffect(() => {
    const mediaQuery =
      typeof window !== "undefined"
        ? window.matchMedia("(max-width: 991px), (pointer: coarse)")
        : null;

    const syncViewport = () => {
      if (!mediaQuery) return;
      setIsMobileQuickToolbarViewport(mediaQuery.matches);
    };

    syncViewport();
    if (!mediaQuery) return;

    mediaQuery.addEventListener("change", syncViewport);
    return () => mediaQuery.removeEventListener("change", syncViewport);
  }, []);

  useEffect(() => {
    setFloatingToolbarPreferences(
      readFloatingToolbarPreferences(floatingToolbarPrefsStorageKey),
    );
    setFloatingToolbarMetrics(
      readJsonFromStorage<FloatingQuickToolbarMetrics>(
        floatingToolbarMetricsStorageKey,
        DEFAULT_FLOATING_TOOLBAR_METRICS,
      ),
    );
    floatingToolbarPrefsHydratedRef.current = true;
  }, [floatingToolbarMetricsStorageKey, floatingToolbarPrefsStorageKey]);

  useEffect(() => {
    if (!floatingToolbarPrefsHydratedRef.current) return;
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        floatingToolbarPrefsStorageKey,
        JSON.stringify(floatingToolbarPreferences),
      );
    } catch {
      // Ignore persistence failures in constrained storage contexts.
    }
  }, [floatingToolbarPreferences, floatingToolbarPrefsStorageKey]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        floatingToolbarMetricsStorageKey,
        JSON.stringify(floatingToolbarMetrics),
      );
    } catch {
      // Ignore persistence failures in constrained storage contexts.
    }
  }, [floatingToolbarMetrics, floatingToolbarMetricsStorageKey]);

  useEffect(() => {
    if (
      floatingToolbarPreferences.enabled &&
      toolbarEnabledAtRef.current === null
    ) {
      toolbarEnabledAtRef.current = Date.now();
    }
  }, [floatingToolbarPreferences.enabled]);

  const floatingToolbarRecentAnnotations = useMemo<
    QuickActionRecentAnnotation[]
  >(() => {
    const categoryMode = floatingToolbarPreferences.categoryMode;
    const visibleItems =
      categoryMode === "all"
        ? annotationListItems
        : annotationListItems.filter((item) => {
            const typeCode = normalizeAnnotationTypeCode(item.typeCode);
            const catalogEntry = annotationTypeByCode.get(typeCode);
            const category = catalogEntry
              ? resolveAnnotationTypeCategory(catalogEntry)
              : isTechnicalImportedAnnotationTypeCode(typeCode) ||
                  typeCode.includes("artifact")
                ? "technical_imported"
                : "clinical";
            return category === categoryMode;
          });

    return [...visibleItems]
      .sort((a, b) => {
        const distanceA = Math.abs(a.startTime - requestedCurrentTime);
        const distanceB = Math.abs(b.startTime - requestedCurrentTime);
        if (distanceA !== distanceB) return distanceA - distanceB;
        return b.startTime - a.startTime;
      })
      .slice(0, 5)
      .sort((a, b) => b.startTime - a.startTime)
      .map((item) => ({
        id: item.id,
        label: formatAnnotationListLabel(
          item.label,
          item.typeCode,
          annotationTypeCatalog,
        ),
        timeLabel: `T+${formatViewerClockTime(item.startTime)}`,
        durationLabel: formatCompactAnnotationDuration(
          item.startTime,
          item.endTime,
        ),
        typeLabel: getAnnotationTypeDisplayName(
          item.typeCode,
          annotationTypeCatalog,
        ),
        color: resolveAnnotationDisplayColor(
          {
            annotation_type: item.typeCode,
            color: item.color,
          },
          item.color,
        ),
        title: `${formatAnnotationListLabel(
          item.label,
          item.typeCode,
          annotationTypeCatalog,
        )} · T+${formatViewerClockTime(item.startTime)} · ${formatCompactAnnotationDuration(
          item.startTime,
          item.endTime,
        )}`,
      }));
  }, [
    annotationListItems,
    annotationTypeByCode,
    annotationTypeCatalog,
    floatingToolbarPreferences.categoryMode,
    requestedCurrentTime,
    resolveAnnotationDisplayColor,
    resolveAnnotationTypeCategory,
  ]);

  const handleFloatingToolbarRecentAnnotationSelect = useCallback(
    (annotation: QuickActionRecentAnnotation) => {
      const item = annotationListItems.find(
        (candidate) => candidate.id === annotation.id,
      );
      if (!item) return;
      jumpToViewerTime(item.startTime, {
        source: "floating-toolbar-recent-annotation",
      });
    },
    [annotationListItems, jumpToViewerTime],
  );

  const updateFloatingToolbarMetrics = useCallback(
    (
      updater: (
        previous: FloatingQuickToolbarMetrics,
      ) => FloatingQuickToolbarMetrics,
    ) => {
      setFloatingToolbarMetrics((previous) => {
        const next = updater(previous);
        try {
          if (typeof window !== "undefined") {
            window.dispatchEvent(
              new CustomEvent("eeg:floating-toolbar-metrics", {
                detail: {
                  userId: toolbarUserId,
                  metrics: next,
                  timestamp: new Date().toISOString(),
                },
              }),
            );
          }
        } catch {
          // Ignore telemetry event emission failures.
        }
        return next;
      });
    },
    [toolbarUserId],
  );

  const showFloatingToolbarPopupBlockedMessage = useCallback(() => {
    const msg =
      'Popup blocked. Allow popups for this site to open the annotation toolbar window.';
    if (popupBlockedTimerRef.current) {
      clearTimeout(popupBlockedTimerRef.current);
    }
    setPopupBlockedMessage(msg);
    popupBlockedTimerRef.current = setTimeout(
      () => setPopupBlockedMessage(null),
      7000,
    );
  }, [popupBlockedTimerRef, setPopupBlockedMessage]);

  const setFloatingToolbarEnabled = useCallback(
    (enabled: boolean) => {
      if (!enabled) {
        floatingToolbarPopout.popIn();
        toolbarEnabledAtRef.current = null;
      }

      setFloatingToolbarPreferences((previous) => ({
        ...previous,
        enabled,
      }));
      if (!enabled) return;
      toolbarEnabledAtRef.current = Date.now();
      updateFloatingToolbarMetrics((previous) => ({
        ...previous,
        openCount: previous.openCount + 1,
      }));
    },
    [
      floatingToolbarPopout,
      updateFloatingToolbarMetrics,
    ],
  );

  const handleFloatingToolbarPopOut = useCallback(() => {
    void (async () => {
      const opened = await floatingToolbarPopout.popOut();
      if (!opened) {
        showFloatingToolbarPopupBlockedMessage();
        return;
      }
      setFloatingToolbarPreferences((previous) => ({
        ...previous,
        enabled: true,
      }));
      toolbarEnabledAtRef.current = Date.now();
      updateFloatingToolbarMetrics((previous) => ({
        ...previous,
        openCount: previous.openCount + 1,
      }));
    })();
  }, [
    floatingToolbarPopout,
    showFloatingToolbarPopupBlockedMessage,
    updateFloatingToolbarMetrics,
  ]);

  const handleFloatingToolbarPopIn = useCallback(() => {
    floatingToolbarPopout.popIn();
    setFloatingToolbarPreferences((previous) => ({
      ...previous,
      enabled: true,
    }));
  }, [floatingToolbarPopout]);

  const handleClinicalDockOpenFloatingToolbar = useCallback(() => {
    floatingToolbarPopout.popIn();
    setFloatingToolbarEnabled(true);
  }, [floatingToolbarPopout, setFloatingToolbarEnabled]);

  const toggleFloatingToolbarEditMode = useCallback(() => {
    setFloatingToolbarPreferences((previous) => ({
      ...previous,
      editMode: !previous.editMode,
    }));
  }, []);

  const handleFloatingToolbarActionsChanged = useCallback(
    (actions: QuickAction[]) => {
      setFloatingToolbarPreferences((previous) => ({
        ...previous,
        actionOrder: actions.map((action) => action.id),
        hiddenActionIds: actions
          .filter((action) => action.enabled === false)
          .map((action) => action.id),
      }));
    },
    [],
  );

  const floatingToolbarEventSetId =
    floatingToolbarPreferences.eventSetId || activeEventSetId || "";

  const floatingToolbarEventItems = useMemo(() => {
    if (!floatingToolbarEventSetId) {
      return activeViewerEventItems;
    }
    const eventSet = viewerEventSets.find(
      (set) => set.id === floatingToolbarEventSetId,
    );
    if (!eventSet) {
      return activeViewerEventItems;
    }
    const configuredIds = new Set(eventSet.eventIds || []);
    return viewerEventCatalog.filter(
      (event) =>
        event.visibleInDock !== false &&
        (configuredIds.has(event.id) ||
          event.eventSetIds.includes(eventSet.id)),
    );
  }, [
    activeViewerEventItems,
    floatingToolbarEventSetId,
    viewerEventCatalog,
    viewerEventSets,
  ]);

  const eventItemForFloatingToolbarType = useCallback(
    (type: AnnotationTypeCatalogEntry) => {
      const typeCode = normalizeAnnotationCatalogLink(type.type_code);
      const displayName = normalizeAnnotationCatalogLink(type.display_name);
      return floatingToolbarEventItems.find((event) => {
        const configuredCode = normalizeAnnotationCatalogLink(
          event.annotationTypeCode,
        );
        const eventId = normalizeAnnotationCatalogLink(event.id);
        const eventName = normalizeAnnotationCatalogLink(event.name);
        if (configuredCode) return configuredCode === typeCode;
        return (
          eventId === typeCode ||
          eventName === displayName ||
          (eventId === "seizure" && typeCode.startsWith("seizure_"))
        );
      });
    },
    [floatingToolbarEventItems],
  );

  const floatingToolbarQuickAddCatalog = useMemo(() => {
    let catalog = annotationTypeCatalog;
    if (floatingToolbarEventSetId && floatingToolbarEventItems.length > 0) {
      const scoped = annotationTypeCatalog.filter((type) =>
        Boolean(eventItemForFloatingToolbarType(type)),
      );
      catalog = scoped.length > 0 ? scoped : annotationTypeCatalog;
    }

    const filteredCatalog =
      floatingToolbarPreferences.categoryMode === "all"
        ? catalog
        : catalog.filter(
            (type) =>
              resolveAnnotationTypeCategory(type) ===
              floatingToolbarPreferences.categoryMode,
          );

    return filteredCatalog
      .filter((type) => {
        const eventItem = eventItemForFloatingToolbarType(type);
        return eventItem?.defaultQuickAction ?? type.quick_action_enabled;
      })
      .sort((a, b) => {
        const eventA = eventItemForFloatingToolbarType(a);
        const eventB = eventItemForFloatingToolbarType(b);
        const configuredOrderA = eventA?.defaultShortcut ?? a.quick_action_order;
        const configuredOrderB = eventB?.defaultShortcut ?? b.quick_action_order;
        const orderA = Number.isFinite(configuredOrderA)
          ? Number(configuredOrderA)
          : Number.MAX_SAFE_INTEGER;
        const orderB = Number.isFinite(configuredOrderB)
          ? Number(configuredOrderB)
          : Number.MAX_SAFE_INTEGER;
        if (orderA !== orderB) return orderA - orderB;
        return a.display_name.localeCompare(b.display_name);
      });
  }, [
    annotationTypeCatalog,
    eventItemForFloatingToolbarType,
    floatingToolbarEventItems.length,
    floatingToolbarEventSetId,
    floatingToolbarPreferences.categoryMode,
    resolveAnnotationTypeCategory,
  ]);

  const floatingToolbarListTypeOptions = useMemo<QuickActionListTypeOption[]>(
    () =>
      viewerEventSets.map((eventSet) => ({
        id: eventSet.id,
        label: eventSet.name,
      })),
    [viewerEventSets],
  );

  const floatingToolbarCategoryModeOptions = useMemo<
    QuickActionListTypeOption[]
  >(
    () => [
      { id: "all", label: "All" },
      { id: "clinical", label: "Clinical" },
      { id: "technical_imported", label: "Technical / artifacts" },
    ],
    [],
  );

  const floatingToolbarActions = useMemo<QuickAction[]>(() => {
    const baseActions: QuickAction[] = [
      ...floatingToolbarQuickAddCatalog.map((item) => ({
        id: item.annotation_type_id,
        icon: (
          <span aria-hidden="true">
            {QUICK_ACTION_BUTTON_EMOJI[item.type_code] || "•"}
          </span>
        ),
        label: item.display_name,
        shortLabel: item.display_name,
        accentColor: item.color,
        supportsDuration: catalogEntrySupportsDuration(item),
        action: () => {
          try {
            addQuickActionAnnotation(item);
            const openedAt = toolbarEnabledAtRef.current;
            updateFloatingToolbarMetrics((previous) => {
              const next = {
                ...previous,
                actionCount: previous.actionCount + 1,
                actionUsage: {
                  ...previous.actionUsage,
                  [item.annotation_type_id]:
                    (previous.actionUsage[item.annotation_type_id] || 0) + 1,
                },
              };
              if (openedAt) {
                const elapsed = Math.max(0, Date.now() - openedAt);
                next.firstActionSamples += 1;
                next.totalFirstActionMs += elapsed;
                toolbarEnabledAtRef.current = null;
              }
              return next;
            });
          } catch (error) {
            updateFloatingToolbarMetrics((previous) => ({
              ...previous,
              errorClicks: previous.errorClicks + 1,
            }));
            logger.error("[EEGViewer] Floating toolbar action failed:", error);
          }
        },
      })),
      {
        id: "templates-macros",
        icon: <span aria-hidden="true">⚡</span>,
        label: "Templates / Macros",
        action: () => {
          try {
            setShowAnnotationQuickOpen(true);
            setTrendPreviewMessage(null);
            setTrendPreviewError(null);
            updateFloatingToolbarMetrics((previous) => {
              const openedAt = toolbarEnabledAtRef.current;
              const next = {
                ...previous,
                actionCount: previous.actionCount + 1,
                actionUsage: {
                  ...previous.actionUsage,
                  "templates-macros":
                    (previous.actionUsage["templates-macros"] || 0) + 1,
                },
              };
              if (openedAt) {
                const elapsed = Math.max(0, Date.now() - openedAt);
                next.firstActionSamples += 1;
                next.totalFirstActionMs += elapsed;
                toolbarEnabledAtRef.current = null;
              }
              return next;
            });
          } catch (error) {
            updateFloatingToolbarMetrics((previous) => ({
              ...previous,
              errorClicks: previous.errorClicks + 1,
            }));
            logger.error(
              "[EEGViewer] Floating toolbar quick-open failed:",
              error,
            );
          }
        },
      },
    ];

    const actionById = new Map(
      baseActions.map((action) => [action.id, action]),
    );
    const ordered: QuickAction[] = [];
    const seenIds = new Set<string>();

    floatingToolbarPreferences.actionOrder.forEach((id) => {
      const action = actionById.get(id);
      if (!action) return;
      ordered.push(action);
      seenIds.add(id);
    });

    baseActions.forEach((action) => {
      if (seenIds.has(action.id)) return;
      ordered.push(action);
    });

    return ordered.map((action) => ({
      ...action,
      enabled: !floatingToolbarPreferences.hiddenActionIds.includes(action.id),
    }));
  }, [
    addQuickActionAnnotation,
    floatingToolbarPreferences.actionOrder,
    floatingToolbarPreferences.hiddenActionIds,
    floatingToolbarQuickAddCatalog,
    setShowAnnotationQuickOpen,
    setTrendPreviewError,
    setTrendPreviewMessage,
    updateFloatingToolbarMetrics,
  ]);

  const renderFloatingAnnotationToolbar = useCallback(
    (mode: "pinned" | "detached" = "pinned") => (
      <Suspense fallback={null}>
      <QuickActionToolbar
        actions={floatingToolbarActions}
        className={
          mode === "pinned" ? "quick-action-toolbar--viewer-pinned" : undefined
        }
        position={floatingToolbarPreferences.position}
        customizable={true}
        dragEnabled={floatingToolbarPreferences.editMode}
        positionDragEnabled={mode === "pinned"}
        layout={mode === "detached" ? "detached" : "floating"}
        useExternalDetachedChrome={mode === "detached"}
        displayMode="labels"
        compactList={true}
        listTypeOptions={floatingToolbarListTypeOptions}
        listTypeId={floatingToolbarEventSetId}
        onListTypeChange={(nextEventSetId) =>
          setFloatingToolbarPreferences((previous) => ({
            ...previous,
            eventSetId: nextEventSetId,
          }))
        }
        listTypeLabel="Annotation set"
        categoryModeOptions={floatingToolbarCategoryModeOptions}
        categoryModeId={floatingToolbarPreferences.categoryMode}
        onCategoryModeChange={(nextCategoryMode) =>
          setFloatingToolbarPreferences((previous) => ({
            ...previous,
            categoryMode: isAnnotationCategoryFilter(nextCategoryMode)
              ? nextCategoryMode
              : DEFAULT_ANNOTATION_CATEGORY_FILTER,
          }))
        }
        categoryModeLabel="Review mode"
        recentAnnotations={floatingToolbarRecentAnnotations}
        onRecentAnnotationSelect={handleFloatingToolbarRecentAnnotationSelect}
        recentAnnotationsLabel="Recent annotations"
        offset={{
          x: floatingToolbarPreferences.offsetX,
          y: floatingToolbarPreferences.offsetY,
        }}
        panelWidthPx={floatingToolbarPreferences.panelWidthPx}
        onPanelWidthChange={(width) =>
          setFloatingToolbarPreferences((previous) => ({
            ...previous,
            panelWidthPx: width,
          }))
        }
        onOffsetChange={(next) =>
          setFloatingToolbarPreferences((previous) => ({
            ...previous,
            offsetX: next.x,
            offsetY: next.y,
          }))
        }
        onActionReorder={handleFloatingToolbarActionsChanged}
        onPopOut={undefined}
        onClose={
          mode === "pinned" ? () => setFloatingToolbarEnabled(false) : undefined
        }
      />
      </Suspense>
    ),
    [
      floatingToolbarActions,
      floatingToolbarCategoryModeOptions,
      floatingToolbarEventSetId,
      floatingToolbarListTypeOptions,
      floatingToolbarPreferences.categoryMode,
      floatingToolbarPreferences.editMode,
      floatingToolbarPreferences.offsetX,
      floatingToolbarPreferences.offsetY,
      floatingToolbarPreferences.panelWidthPx,
      floatingToolbarPreferences.position,
      floatingToolbarRecentAnnotations,
      handleFloatingToolbarRecentAnnotationSelect,
      handleFloatingToolbarActionsChanged,
      setFloatingToolbarEnabled,
    ],
  );

  const floatingToolbarAverageTimeToActionMs = useMemo(() => {
    if (floatingToolbarMetrics.firstActionSamples <= 0) return null;
    return Math.round(
      floatingToolbarMetrics.totalFirstActionMs /
        floatingToolbarMetrics.firstActionSamples,
    );
  }, [
    floatingToolbarMetrics.firstActionSamples,
    floatingToolbarMetrics.totalFirstActionMs,
  ]);

  useEffect(() => {
    const hasAnyUsageSignal =
      floatingToolbarMetrics.openCount > 0 ||
      floatingToolbarMetrics.actionCount > 0 ||
      floatingToolbarMetrics.errorClicks > 0;
    if (!hasAnyUsageSignal) return;

    const payload: FloatingToolbarTelemetryPayload = {
      metrics: {
        open_count: floatingToolbarMetrics.openCount,
        action_count: floatingToolbarMetrics.actionCount,
        error_clicks: floatingToolbarMetrics.errorClicks,
        first_action_samples: floatingToolbarMetrics.firstActionSamples,
        total_first_action_ms: floatingToolbarMetrics.totalFirstActionMs,
        avg_first_action_ms: floatingToolbarAverageTimeToActionMs,
        action_usage: floatingToolbarMetrics.actionUsage,
      },
      context: {
        toolbar_enabled: floatingToolbarPreferences.enabled,
        edit_mode: floatingToolbarPreferences.editMode,
        mobile_simplified: isMobileQuickToolbarViewport,
        ...(recordingId ? { recording_id: recordingId } : {}),
        ...(studyId ? { study_id: studyId } : {}),
      },
    } as const;
    const payloadHash = JSON.stringify(payload);

    if (payloadHash === floatingToolbarLastTelemetryHashRef.current) {
      return;
    }

    if (floatingToolbarTelemetryFlushTimeoutRef.current) {
      clearTimeout(floatingToolbarTelemetryFlushTimeoutRef.current);
    }

    floatingToolbarPendingTelemetryRef.current = {
      hash: payloadHash,
      payload,
    };
    floatingToolbarTelemetryFlushTimeoutRef.current = setTimeout(() => {
      const pending = floatingToolbarPendingTelemetryRef.current;
      if (!pending) return;
      floatingToolbarLastTelemetryHashRef.current = pending.hash;
      void trackFloatingToolbarMetrics(pending.payload);
      floatingToolbarPendingTelemetryRef.current = null;
      floatingToolbarTelemetryFlushTimeoutRef.current = null;
    }, 2500);

    return () => {
      if (floatingToolbarTelemetryFlushTimeoutRef.current) {
        clearTimeout(floatingToolbarTelemetryFlushTimeoutRef.current);
        floatingToolbarTelemetryFlushTimeoutRef.current = null;
      }
    };
  }, [
    floatingToolbarAverageTimeToActionMs,
    floatingToolbarMetrics.actionCount,
    floatingToolbarMetrics.actionUsage,
    floatingToolbarMetrics.errorClicks,
    floatingToolbarMetrics.firstActionSamples,
    floatingToolbarMetrics.openCount,
    floatingToolbarMetrics.totalFirstActionMs,
    floatingToolbarPreferences.editMode,
    floatingToolbarPreferences.enabled,
    isMobileQuickToolbarViewport,
    recordingId,
    studyId,
  ]);

  useEffect(
    () => () => {
      if (floatingToolbarTelemetryFlushTimeoutRef.current) {
        clearTimeout(floatingToolbarTelemetryFlushTimeoutRef.current);
        floatingToolbarTelemetryFlushTimeoutRef.current = null;
      }
      const pending = floatingToolbarPendingTelemetryRef.current;
      if (
        pending &&
        pending.hash !== floatingToolbarLastTelemetryHashRef.current
      ) {
        floatingToolbarLastTelemetryHashRef.current = pending.hash;
        void trackFloatingToolbarMetrics(pending.payload);
      }
      floatingToolbarPendingTelemetryRef.current = null;
    },
    [],
  );

  return {
    isMobileQuickToolbarViewport,
    floatingToolbarPreferences,
    setFloatingToolbarPreferences,
    floatingToolbarMetrics,
    setFloatingToolbarMetrics,
    floatingToolbarPopout,
    floatingToolbarRecentAnnotations,
    handleFloatingToolbarRecentAnnotationSelect,
    updateFloatingToolbarMetrics,
    setFloatingToolbarEnabled,
    handleFloatingToolbarPopOut,
    handleFloatingToolbarPopIn,
    handleClinicalDockOpenFloatingToolbar,
    toggleFloatingToolbarEditMode,
    handleFloatingToolbarActionsChanged,
    floatingToolbarEventSetId,
    floatingToolbarEventItems,
    eventItemForFloatingToolbarType,
    floatingToolbarQuickAddCatalog,
    floatingToolbarListTypeOptions,
    floatingToolbarCategoryModeOptions,
    floatingToolbarActions,
    renderFloatingAnnotationToolbar,
    floatingToolbarAverageTimeToActionMs,
  };
}
